import type { ReactElement } from 'react';
import { Link } from 'react-router';

import { Badge } from '@autop/ui/src/badge';

import { AssetDownloadButton } from '@/features/download-asset';
import { assetMetaOf, type MyAsset, useMyAssets } from '@/entities/asset';
import { useSession } from '@/entities/session';
import { errorCodeOf } from '@/shared/api';
import { loginPathWithNext } from '@/shared/lib/auth-paths';

import { MyShell } from '../../shell';
import { SignedOutNotice } from '../../signed-out-notice';

const RESUME_PATH = '/my/downloads';

function MyAssetRow({ item }: { item: MyAsset }): ReactElement {
  return (
    <li className="flex flex-wrap items-center gap-3 border-border border-b p-4 last:border-b-0">
      <Badge variant="mint">무료</Badge>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[14.5px] font-medium">{item.title}</span>
        <span className="text-[12.5px] text-muted-foreground">
          {item.available && item.kindLabel !== null && item.format !== null
            ? assetMetaOf({
                kindLabel: item.kindLabel,
                format: item.format,
                sizeBytes: item.sizeBytes,
              })
            : '더 이상 제공되지 않는 자료입니다'}
        </span>
      </div>
      {item.available ? (
        <AssetDownloadButton
          assetId={item.assetId}
          resumePath={RESUME_PATH}
          isLink={item.format === 'LINK'}
          label={item.format === 'LINK' ? '다시 열기' : '다시 받기'}
        />
      ) : null}
    </li>
  );
}

/**
 * 받은 자료 — 1차 오픈 범위 §3.1 O-09 (/my/downloads).
 *
 * 서버 다운로드 기록이 원본이다 (LIB-U-04 AC-3) — 기기를 바꿔도, 로그아웃했다
 * 돌아와도 자료함은 그대로다. 자료실과 **같은 축(영상)** 으로 묶는다.
 */
export function MyAssetsPage(): ReactElement {
  const member = useSession((state) => state.member);
  const myAssets = useMyAssets(member !== null);

  if (member === null) {
    return <SignedOutNotice />;
  }

  const items = myAssets.data?.items ?? [];
  // 영상이 살아 있는 자료는 영상 단위로, 영상까지 사라진 자료는 마지막에 평평하게
  const videoGroups = new Map<string, { title: string; items: MyAsset[] }>();
  const orphanItems: MyAsset[] = [];
  for (const item of items) {
    if (item.video === null) {
      orphanItems.push(item);
      continue;
    }
    const group = videoGroups.get(item.video.id) ?? { title: item.video.title, items: [] };
    videoGroups.set(item.video.id, { ...group, items: [...group.items, item] });
  }

  return (
    <MyShell title="받은 자료" description="받으신 자료는 언제든 다시 여실 수 있습니다">
      {myAssets.isPending ? (
        <div aria-busy="true" className="h-48 animate-pulse rounded-lg bg-secondary" />
      ) : myAssets.isError ? (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-border border-dashed p-10">
          {errorCodeOf(myAssets.error) === 'UNAUTHORIZED' ? (
            <>
              <p className="text-[15px]">로그인이 만료되었습니다.</p>
              <Link
                to={loginPathWithNext(RESUME_PATH)}
                className="mt-1 inline-flex h-10 items-center rounded-md border border-foreground px-4 text-sm font-medium"
              >
                다시 로그인
              </Link>
            </>
          ) : (
            <>
              <p className="text-[15px]">자료함을 불러오지 못했습니다.</p>
              <button
                type="button"
                onClick={() => void myAssets.refetch()}
                className="text-[13px] underline underline-offset-4"
              >
                다시 시도
              </button>
            </>
          )}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-border border-dashed p-10">
          <p className="text-[15px]">아직 받으신 자료가 없습니다.</p>
          <p className="text-[13.5px] text-muted-foreground">
            자료실에서 영상별로 정리된 자료를 받아보세요.
          </p>
          <Link
            to="/library"
            className="mt-1 inline-flex h-10 items-center rounded-md border border-foreground px-4 text-sm font-medium"
          >
            자료실 가기
          </Link>
        </div>
      ) : (
        <ul className="flex flex-col gap-5">
          {[...videoGroups.entries()].map(([videoId, group]) => (
            <li key={videoId} className="overflow-hidden rounded-lg border border-border">
              <p className="border-border border-b bg-secondary/40 px-4 py-2 text-[12.5px] font-medium text-muted-foreground">
                {group.title}
              </p>
              <ul>
                {group.items.map((item) => (
                  <MyAssetRow key={item.assetId} item={item} />
                ))}
              </ul>
            </li>
          ))}
          {orphanItems.length > 0 ? (
            <li className="overflow-hidden rounded-lg border border-border">
              <p className="border-border border-b bg-secondary/40 px-4 py-2 text-[12.5px] font-medium text-muted-foreground">
                공개 종료된 자료
              </p>
              <ul>
                {orphanItems.map((item) => (
                  <MyAssetRow key={item.assetId} item={item} />
                ))}
              </ul>
            </li>
          ) : null}
        </ul>
      )}
    </MyShell>
  );
}
